/**
 * CobrosPage — Bandeja unica de Cobranza
 *
 * Fusiona lo que antes eran dos pantallas separadas (Por Cobrar + Agendados):
 * un continuo temporal desde lo mas vencido hasta lo mas futuro, en una sola
 * tabla con semaforo de 5 colores por urgencia.
 *
 * Universo unificado:
 *  - Cupon vivo por credito (de useBackofficeCredits) — cubre vencidos, hoy y
 *    el proximo cupon por vencer de cada credito.
 *  - Cupones futuros del schedule completo (de useScheduledCoupons) — cubre
 *    cupones 2/12, 3/12, ... que aun no son el "vivo".
 *
 * La union se hace por (credit_id, coupon_number) — si un cupon aparece en
 * ambos, gana el vivo (tiene mas metadata como telefono).
 *
 * Castigados y Cobrados NO viven aqui — cada uno tiene su pantalla.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Search, User } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROUTES, PARTNER_ID } from '@/constants';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { useScheduledCoupons } from '@/features/collection/hooks/useScheduledCoupons';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';
import type { ScheduledCoupon } from '@/types/collection.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
];

// ─── Types ────────────────────────────────────────────────────────────────

type Rango = 'todo' | 'vencidos' | 'hoy' | 'prox7' | 'prox30';

interface CobranzaRow {
  key: string;               // credit_id + coupon_number
  creditId: string;
  directoryId: string;
  fullName: string;
  phone: string;
  couponNumber: number;
  couponTotal: number;
  amount: number;            // saldo pendiente del cupon
  couponTotalMxn: number;    // total nominal (para mostrar "de X" si es parcial)
  isPartialPaid: boolean;
  dueDate: string;           // YYYY-MM-DD
  daysUntilDue: number;      // negativo = vencido, 0 = hoy, positivo = futuro
}

// ─── Semaforo ─────────────────────────────────────────────────────────────

type Semaforo = {
  color: 'red' | 'orange' | 'yellow' | 'blue' | 'green';
  label: string;
  dotClass: string;
  badgeClass: string;
};

function semaforo(days: number): Semaforo {
  if (days < -7) return {
    color: 'red',
    label: `Vencido ${-days}d`,
    dotClass: 'bg-red-500',
    badgeClass: 'bg-red-900/40 text-red-300',
  };
  if (days < 0) return {
    color: 'orange',
    label: `Vencido ${-days}d`,
    dotClass: 'bg-orange-500',
    badgeClass: 'bg-orange-900/40 text-orange-300',
  };
  if (days === 0) return {
    color: 'yellow',
    label: 'Vence hoy',
    dotClass: 'bg-yellow-400',
    badgeClass: 'bg-yellow-900/40 text-yellow-300',
  };
  if (days <= 7) return {
    color: 'blue',
    label: `En ${days}d`,
    dotClass: 'bg-blue-500',
    badgeClass: 'bg-blue-900/40 text-blue-300',
  };
  return {
    color: 'green',
    label: `En ${days}d`,
    dotClass: 'bg-emerald-500',
    badgeClass: 'bg-emerald-900/40 text-emerald-300',
  };
}

// ─── Utils ────────────────────────────────────────────────────────────────

function daysUntil(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(iso);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function formatDateShort(iso: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function liveCouponToRow(c: BackofficeCreditsListItem): CobranzaRow | null {
  if (!c.next_due_date) return null;
  return {
    key: `${c.credit_id}:${(c.weeks_paid ?? 0) + 1}`,
    creditId: c.credit_id,
    directoryId: c.directory_id,
    fullName: c.full_name || '—',
    phone: c.phone_number || '',
    couponNumber: (c.weeks_paid ?? 0) + 1,
    couponTotal: c.weeks_total ?? c.term_weeks ?? 0,
    amount: c.next_due_pending_amount_mxn ?? c.weekly_payment_mxn ?? 0,
    couponTotalMxn: c.next_due_total_mxn ?? c.weekly_payment_mxn ?? 0,
    isPartialPaid: c.next_due_partial_paid ?? false,
    dueDate: c.next_due_date,
    daysUntilDue: daysUntil(c.next_due_date),
  };
}

function scheduledCouponToRow(s: ScheduledCoupon, creditPhone?: string): CobranzaRow {
  return {
    key: `${s.credit_id}:${s.coupon_number}`,
    creditId: s.credit_id,
    directoryId: s.client.directory_id,
    fullName: s.client.display_name || '—',
    phone: creditPhone || '',
    couponNumber: s.coupon_number,
    couponTotal: s.coupon_total,
    amount: s.pending_amount_mxn,
    couponTotalMxn: s.nominal_amount_mxn,
    isPartialPaid: s.is_partial_paid,
    dueDate: s.due_date,
    daysUntilDue: daysUntil(s.due_date),
  };
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function CobrosPage() {
  const { credits, isLoading: creditsLoading, refetch: refetchCredits } = useBackofficeCredits();
  const { coupons: scheduled, isLoading: scheduledLoading, refetch: refetchScheduled } = useScheduledCoupons(PARTNER_ID);
  const isLoading = creditsLoading || scheduledLoading;
  const refetch = () => { refetchCredits(); refetchScheduled(); };

  const [rango, setRango] = useState<Rango>('todo');
  const [search, setSearch] = useState('');

  // Union deduplicada: cupon vivo gana sobre scheduled cuando (credit_id, coupon_number) coincide.
  const rows: CobranzaRow[] = useMemo(() => {
    const map = new Map<string, CobranzaRow>();
    const phoneByCredit = new Map<string, string>();
    for (const c of credits) {
      const s = c.status?.toUpperCase();
      if (s === 'DEFAULTED') continue;               // Castigados viven en su propia pantalla
      if (c.phone_number) phoneByCredit.set(c.credit_id, c.phone_number);
      if (s !== 'ACTIVE' && s !== 'DELINQUENT') continue;
      const row = liveCouponToRow(c);
      if (row) map.set(row.key, row);
    }
    for (const s of scheduled) {
      const key = `${s.credit_id}:${s.coupon_number}`;
      if (map.has(key)) continue;                    // vivo gana
      map.set(key, scheduledCouponToRow(s, phoneByCredit.get(s.credit_id)));
    }
    return Array.from(map.values());
  }, [credits, scheduled]);

  // Filtro por rango
  const rowsInRange = useMemo(() => {
    switch (rango) {
      case 'vencidos': return rows.filter(r => r.daysUntilDue < 0);
      case 'hoy':      return rows.filter(r => r.daysUntilDue === 0);
      case 'prox7':    return rows.filter(r => r.daysUntilDue >= 0 && r.daysUntilDue <= 7);
      case 'prox30':   return rows.filter(r => r.daysUntilDue >= 0 && r.daysUntilDue <= 30);
      case 'todo':
      default:         return rows;
    }
  }, [rows, rango]);

  // Busqueda
  const rowsFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rowsInRange;
    return rowsInRange.filter(r =>
      r.fullName.toLowerCase().includes(q) ||
      r.phone.toLowerCase().includes(q) ||
      r.creditId.toLowerCase().includes(q)
    );
  }, [rowsInRange, search]);

  // Ordenamiento default: por urgencia = por fecha ascendente
  // (mas viejos vencidos arriba, luego hoy, luego futuro cercano, luego futuro lejano)
  const rowsSorted = useMemo(() => {
    return [...rowsFiltered].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }, [rowsFiltered]);

  const totalMonto = rowsSorted.reduce((s, r) => s + r.amount, 0);

  // Conteo de castigados para el link al pie
  const castigadosCount = useMemo(
    () => credits.filter(c => c.status?.toUpperCase() === 'DEFAULTED').length,
    [credits]
  );

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Cobranza" subtitle="Vencidos y agenda futura" breadcrumbs={BREADCRUMBS} />
        <Skeleton height={400} variant="rectangular" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Cobranza"
        subtitle={`Vencidos y agenda futura · ${formatCurrency(totalMonto, 'MXN', 'es-MX')} en ${formatNumber(rowsSorted.length)} cupones`}
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={refetch}>
            <RefreshCw size={16} /> Actualizar
          </Button>
        }
      />

      <div className="space-y-4">
        {/* Toggle rango + busqueda */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            <RangoTab label="Todo"          active={rango === 'todo'}     onClick={() => setRango('todo')} />
            <RangoTab label="Vencidos"      active={rango === 'vencidos'} onClick={() => setRango('vencidos')} />
            <RangoTab label="Hoy"           active={rango === 'hoy'}      onClick={() => setRango('hoy')} />
            <RangoTab label="Próximos 7d"   active={rango === 'prox7'}    onClick={() => setRango('prox7')} />
            <RangoTab label="Próximos 30d"  active={rango === 'prox30'}   onClick={() => setRango('prox30')} />
          </div>

          <div className="flex min-w-[280px] items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5">
            <Search size={14} className="text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nombre, teléfono o ID de crédito..."
              className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
        </div>

        {/* Tabla */}
        <Card>
          {rowsSorted.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No hay cupones que cumplan los filtros.
            </p>
          ) : (
            <CuponTable rows={rowsSorted.slice(0, 100)} total={rowsSorted.length} />
          )}
        </Card>

        {/* Link a Castigados */}
        <div className="flex justify-end">
          <Link
            to={ROUTES.COLLECTION_CASTIGADOS}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
          >
            <span className="h-2 w-2 rounded-full bg-zinc-500" />
            Ver créditos castigados
            {castigadosCount > 0 && (
              <span className="rounded bg-zinc-500/20 px-1.5 py-0.5 text-[10px] font-bold text-zinc-300">
                {castigadosCount}
              </span>
            )}
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Rango tabs ───────────────────────────────────────────────────────────

function RangoTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Tabla ────────────────────────────────────────────────────────────────

function CuponTable({ rows, total }: { rows: CobranzaRow[]; total: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr>
            <th className="w-2 px-1" />
            <th className="px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cliente</th>
            <th className="px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Crédito</th>
            <th className="px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cupón</th>
            <th className="px-2.5 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Monto</th>
            <th className="px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Vencimiento</th>
            <th className="px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Estado</th>
            <th className="w-9" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => <CuponTr key={r.key} row={r} />)}
        </tbody>
      </table>

      <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
        <span>Mostrando {Math.min(rows.length, 100)} de {total} cupones</span>
        {total > 100 && <span className="text-muted-foreground/70">Use rango o búsqueda para acotar</span>}
      </div>
    </div>
  );
}

function CuponTr({ row }: { row: CobranzaRow }) {
  const detailLink = `/credit/detail/${row.creditId}?directoryId=${row.directoryId}&from=cobranza`;
  const expedienteLink = `/backoffice/expedientes/${row.directoryId}?from=cobranza`;
  const s = semaforo(row.daysUntilDue);
  const labelText = row.isPartialPaid ? `${s.label} · Parcial` : s.label;

  return (
    <tr className="cursor-pointer border-b border-border/30 hover:bg-accent/30">
      <td className="px-1 py-2">
        <span className={`inline-block h-2 w-2 rounded-full ${s.dotClass}`} aria-hidden />
      </td>
      <td className="px-2.5 py-2">
        <Link to={detailLink} className="block">
          <div className="font-medium text-foreground">{row.fullName}</div>
          <div className="text-[10px] text-muted-foreground">{row.phone || '—'}</div>
        </Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={detailLink} className="block font-mono text-[10px] text-muted-foreground">
          {row.creditId.slice(0, 12)}
        </Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={detailLink} className="block text-foreground">
          {row.couponNumber} / {row.couponTotal}
        </Link>
      </td>
      <td className="px-2.5 py-2 text-right tabular-nums text-foreground">
        <Link to={detailLink} className="block">
          {formatCurrency(row.amount, 'MXN', 'es-MX')}
          {row.isPartialPaid && (
            <div className="text-[10px] font-normal text-muted-foreground">
              de {formatCurrency(row.couponTotalMxn, 'MXN', 'es-MX')}
            </div>
          )}
        </Link>
      </td>
      <td className="px-2.5 py-2 text-foreground">
        <Link to={detailLink} className="block">{formatDateShort(row.dueDate)}</Link>
      </td>
      <td className="px-2.5 py-2">
        <Link to={detailLink} className="block">
          <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.badgeClass}`}>
            {labelText}
          </span>
        </Link>
      </td>
      <td className="px-1 py-2 text-right">
        <Link
          to={expedienteLink}
          title="Ver expediente del cliente"
          aria-label="Ver expediente del cliente"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <User size={14} />
        </Link>
      </td>
    </tr>
  );
}
